import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ArrowRight, Sparkles } from "lucide-react";
import { Link } from "react-router-dom";
import { useState, useEffect, memo } from "react";
import heroBefore from "@/assets/hero-before.avif";
import heroAfter from "@/assets/hero-after.jpg";

export const HeroSection = memo(function HeroSection() {
  const [showAfter, setShowAfter] = useState(false);

  // Auto-flip every 3 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setShowAfter(prev => !prev);
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  return (
    <section className="relative min-h-screen flex items-center pt-16 gradient-hero overflow-hidden">
      {/* Background decoration */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-primary/5 rounded-full blur-3xl will-change-transform" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-primary/10 rounded-full blur-3xl will-change-transform" />
      </div>

      <div className="container mx-auto px-4 py-20 relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left content */}
          <div className="space-y-8 animate-fade-in">
            <Badge variant="secondary" className="bg-accent text-primary border-primary/20 px-4 py-2">
              <Sparkles className="w-4 h-4 mr-2" />
              +40% de conversão em vendas
            </Badge>

            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight">
              Vista suas clientes{" "}
              <span className="text-gradient">antes da compra</span>
            </h1>

            <p className="text-lg text-muted-foreground max-w-lg">
              Transforme fotos de roupas em modelos usando IA. 
              Aumente suas vendas mostrando como suas peças ficam em diferentes tipos de corpo.
            </p>

            <div className="flex flex-col sm:flex-row gap-4">
              <Button size="lg" className="gradient-primary border-0 shadow-elegant" asChild>
                <Link to="/auth?tab=signup">
                  Começar Agora
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Link>
              </Button>
              <Button size="lg" variant="outline" asChild>
                <a href="#como-funciona">Como Funciona</a>
              </Button>
            </div>

            <div className="flex items-center gap-8 pt-4">
              <div>
                <p className="text-2xl font-bold text-foreground">100+</p>
                <p className="text-sm text-muted-foreground">Lojas ativas</p>
              </div>
              <div className="w-px h-10 bg-border" />
              <div>
                <p className="text-2xl font-bold text-foreground">10k+</p>
                <p className="text-sm text-muted-foreground">Imagens geradas</p>
              </div>
              <div className="w-px h-10 bg-border" />
              <div>
                <p className="text-2xl font-bold text-foreground">4.9</p>
                <p className="text-sm text-muted-foreground">Avaliação</p>
              </div>
            </div>
          </div>

          {/* Right content - Interactive 3D flip before/after */}
          <div className="relative animate-fade-in-up">
            {/* Toggle indicator */}
            <div className="flex items-center justify-center gap-4 mb-6">
              <button
                onClick={() => setShowAfter(false)}
                className={`text-xs font-medium uppercase tracking-wide transition-colors ${!showAfter ? 'text-foreground' : 'text-muted-foreground'}`}
              >
                ANTES
              </button>
              <div className="flex items-center gap-2">
                <div className={`w-2 h-2 rounded-full transition-colors ${!showAfter ? 'bg-primary' : 'bg-muted'}`} />
                <ArrowRight className="w-4 h-4 text-muted-foreground" />
                <div className={`w-2 h-2 rounded-full transition-colors ${showAfter ? 'bg-primary' : 'bg-muted'}`} />
              </div>
              <button
                onClick={() => setShowAfter(true)}
                className={`text-xs font-medium uppercase tracking-wide flex items-center gap-1 transition-colors ${showAfter ? 'text-primary' : 'text-muted-foreground'}`}
              >
                <Sparkles className="w-3 h-3" />
                COM IA
              </button>
            </div>

            <div 
              className="relative cursor-pointer group perspective-1000 max-w-[280px] mx-auto"
              onClick={() => setShowAfter(!showAfter)}
            >
              {/* Floating conversion badge */}
              <div className="absolute -right-4 -top-4 z-20">
                <Badge className="gradient-primary border-0 text-white px-4 py-2 shadow-lg text-sm">
                  +40% conversão
                </Badge>
              </div>

              {/* 3D Flip Card Container */}
              <div 
                className={`relative transform-style-3d transition-all duration-700 ease-out will-change-transform ${
                  showAfter ? 'rotate-y-180' : ''
                }`}
              >
                {/* Front Side - Before */}
                <div className={`relative aspect-[3/4] rounded-xl overflow-hidden shadow-card ring-1 ring-border backface-hidden bg-card ${
                  showAfter ? 'invisible' : 'visible'
                }`}>
                  <img 
                    src={heroBefore}
                    alt="Foto do produto"
                    className="w-full h-full object-cover"
                    loading="eager"
                    fetchPriority="high"
                    decoding="async"
                  />
                  
                  {/* Label overlay */}
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/50 to-transparent p-4">
                    <span className="text-sm font-medium text-white">Foto da peça</span>
                  </div>
                </div>

                {/* Back Side - After */}
                <div className={`absolute inset-0 aspect-[3/4] rounded-xl overflow-hidden shadow-card ring-2 ring-primary/30 shadow-glow backface-hidden rotate-y-180 ${
                  showAfter ? 'visible' : 'invisible'
                }`}>
                  <img 
                    src={heroAfter}
                    alt="Modelo vestindo a peça"
                    className="w-full h-full object-cover"
                    loading="eager"
                    fetchPriority="high"
                    decoding="async"
                  />
                  
                  {/* Label overlay */}
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-primary/70 to-transparent p-4">
                    <div className="flex items-center gap-2 text-white">
                      <Sparkles className="w-3 h-3" />
                      <span className="text-sm font-medium">Visualização com IA</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Click to toggle text */}
              <p className="text-center text-sm text-muted-foreground mt-4">
                Clique para alternar
              </p>

              {/* Decorative elements */}
              <div className="absolute -top-4 -right-4 w-24 h-24 bg-primary/20 rounded-full blur-2xl pointer-events-none" />
              <div className="absolute -bottom-4 -left-4 w-32 h-32 bg-primary/10 rounded-full blur-2xl pointer-events-none" />
            </div>

            {/* Floating badge */}
            <div className="absolute -right-4 top-1/3 bg-card border border-border rounded-lg p-3 shadow-card animate-float will-change-transform z-10">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full gradient-primary flex items-center justify-center">
                  <Sparkles className="w-4 h-4 text-white" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Imagens realistas</p>
                  <p className="text-sm font-semibold">Venda mais!</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
});
